import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useMemo } from 'react';
import { useDeleteAppStoreChannel } from '../../../api/delete-app-store-channel.api';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_APP_STORE_CHANNEL_QUERY_KEY } from '../../../api/get-list-app-store-channel.api';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { useChannelModalRootActionsStore } from '@/features/channels/_shared/components/channel-modal-root/channel-modal-root.store';
import ModalWithConfirmation from '@/components/_ui/modal-with-confirmation/modal-with-confirmation';

const TOAST_MESSAGES_DELETE_APP_STORE_CHANNEL = {
  success: 'The channel integration has been successfully deleted!',
  error: 'Failed to delete app store channel integration!',
};

export type TDeleteAppStoreChannelModalType = 'DeleteAppStoreChannelModal';
export interface IDeleteAppStoreChannelModalProps {
  organizationChannelId: string;
}

const DeleteAppStoreChannelModal = memo(({ organizationChannelId }: IDeleteAppStoreChannelModalProps) => {
  const showToast = useCustomToast();
  const channelModalRootActions = useChannelModalRootActionsStore();

  const isOpenDeleteAppStoreChannelModal = useMemo(() => {
    return channelModalRootActions.checkIsOpenModal('DeleteAppStoreChannelModal');
  }, [channelModalRootActions]);

  const mutationDeleteIntegrationAppStoreChannel = useDeleteAppStoreChannel({
    mutationConfig: {
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error) || TOAST_MESSAGES_DELETE_APP_STORE_CHANNEL.error;
        showToast({ type: 'error', message: errorMessage });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_APP_STORE_CHANNEL_QUERY_KEY] })]);
        channelModalRootActions.closeModal('DeleteAppStoreChannelModal');
        showToast({ type: 'success', message: TOAST_MESSAGES_DELETE_APP_STORE_CHANNEL.success });
      },
    },
  });

  const handleDelete = useCallback(() => {
    mutationDeleteIntegrationAppStoreChannel.mutate({
      organizationChannelId: organizationChannelId || '',
    });
  }, [mutationDeleteIntegrationAppStoreChannel, organizationChannelId]);

  const handleClose = useCallback(() => {
    channelModalRootActions.closeModal('DeleteAppStoreChannelModal');
  }, [channelModalRootActions]);

  return (
    <ModalWithConfirmation
      isOpen={isOpenDeleteAppStoreChannelModal}
      onClose={handleClose}
      onDelete={handleDelete}
      isLoading={mutationDeleteIntegrationAppStoreChannel.isPending}
      title="Delete App Store Integration"
      description="Do you want to delete this integration?"
      bannerTitle="Deleting this channel will permanently delete all related data, including chat history and analytics."
      confirmText="delete my channel"
    />
  );
});

DeleteAppStoreChannelModal.displayName = 'DeleteAppStoreChannelModal';

export default DeleteAppStoreChannelModal;
